<template>
  <div class="row items-center no-wrap">
    <q-btn
      v-bind="$attrs"
      class="picker"
      :class="[
        contentClass,
        backgroundClass,
        `border-${borderColor}`,
        isHovering && !isDragging && !isUploading && !hoveringOverClear ? 'hover' : undefined,
      ]"
      flat
      :loading="isUploading"
      padding="0"
      :round="round"
      :style="contentStyle"
      text-color="primary"
      @click="pickFiles"
      @dragenter.prevent="onDragEnter"
      @dragleave="onDragLeave"
      @dragover.prevent="onDragOver"
      @drop.prevent="onDrop"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      @mouseover="onMouseOver"
    >
      <div
        class="relative row items-center no-wrap full-width full-height max-h-full"
        :class="{
          'button-layout': clearButtonVisible,
          round,
        }"
      >
        <div class="image-container row justify-center items-center q-py-xs q-px-sm">
          <img
            v-if="modelValue && !isCalculatingDimensions && !isDragging"
            class="image"
            data-t="image"
            :src="modelValue"
            :style="{ 'max-height': maxHeight, 'max-width': maxWidth }"
          />
          <div v-else class="row justify-center items-center">
            <slot v-if="!isDragging" name="upload-message" />
            <div
              v-else
              class="justify-center items-center line-height-1 text-white"
              :class="{
                'row no-wrap text-no-wrap': uploadMessageWidth >= 130,
                'column no-wrap': uploadMessageWidth < 130,
              }"
            >
              <q-icon :name="multiUpload || invalidUpload ? 'block' : 'cloud_upload'" />
              <div :class="{ 'q-ml-xs': uploadMessageWidth > 130 }">
                <div v-if="multiUpload">Please select only one image</div>
                <div v-else-if="invalidUpload">Invalid image</div>
                <div v-else>Drop to upload</div>
              </div>
            </div>
          </div>
          <q-resize-observer @resize="onImageContainerResize" />
        </div>
        <q-file
          ref="imageInput"
          v-model="newImage"
          accept="image/*"
          class="hidden"
          clearable
          filled
          :max-files="1"
          @update:model-value="onImageUpdate"
        />
        <q-btn
          v-if="modelValue || clearButtonVisible"
          class="edit-button absolute-full bg-white"
          color="primary"
          flat
          :round="round"
          :square="!round"
        >
          <slot name="upload-message" />
          <ToolTip v-if="tooltip && !readonly && !disable" class="text-body2">
            {{ tooltip }}
          </ToolTip>
        </q-btn>
      </div>
      <ToolTip v-if="tooltip && !readonly && !disable && !clearButtonVisible" class="text-body2">
        {{ tooltip }}
      </ToolTip>
    </q-btn>
    <div v-if="modelValue || revert" class="q-ml-xs">
      <q-btn
        v-if="!hideClear && clearButtonVisible"
        class="clear-button"
        :class="{ delete: modelValue, revert }"
        :color="revert ? 'primary' : 'negative'"
        flat
        :icon="revert ? 'undo' : 'delete'"
        padding="4px"
        square
        stack
        @click.stop="onClearClick"
        @mouseenter.stop="hoveringOverClear = true"
        @mouseleave.stop="hoveringOverClear = false"
      >
        <ToolTip v-if="revert" anchor="bottom middle" :offset="[0, 4]" self="top middle">Undo Change</ToolTip>
      </q-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { QBtn, type QFile } from 'quasar';
import { computed, onMounted, ref, useTemplateRef, watch, type AllowedComponentProps } from 'vue';
import { useStore } from 'vuex';
import ToolTip from 'components/ToolTip.vue';
import { upload } from 'src/services/cloudinary';
import { setTimeoutPromise } from 'src/services/setTimeout';

defineOptions({ name: 'PictureChooser' });

const props = withDefaults(
  defineProps<{
    bgColor?: string;
    buttonText?: string;
    contentClass?: AllowedComponentProps['class'];
    contentStyle?: AllowedComponentProps['style'];
    disable?: boolean;
    height?: number;
    modelValue?: string | null;
    readonly?: boolean;
    revert?: boolean;
    round?: boolean;
    tooltip?: string;
    type?: string;
    width?: number;
  }>(),
  {
    bgColor: 'form-field',
  }
);

const emit = defineEmits(['clear', 'revert', 'update:model-value', 'upload', 'uploaded']);

const store = useStore();

const isTouchOnly = computed(() => store.state.env.browserSupport.isTouchOnly);

const imageInput = useTemplateRef<QFile>('imageInput');

const newImage = ref<File | null>(null);
const bgColor = ref<'white' | 'black' | 'primary'>('primary');
const hideClear = ref(false);
const isUploading = ref(false);

const borderColor = computed(() => (bgColor.value === 'white' ? 'black' : 'white'));
const clearButtonVisible = computed(() => props.modelValue || props.revert || isUploading.value);
const backgroundClass = computed(() => {
  if (isDragging.value) {
    if (invalidUpload.value || multiUpload.value) {
      return 'bg-negative';
    }
    return 'bg-positive';
  }
  return `bg-${props.modelValue && !isUploading.value ? props.bgColor : 'white'}`;
});

const calculatedWidth = ref(0);
const calculatedHeight = ref(0);
const isCalculatingDimensions = ref(false);

const maxHeight = computed(() => {
  if (props.height) {
    return `${props.height}px`;
  }
  if (calculatedHeight.value) {
    return `${calculatedHeight.value}px`;
  }
  return '100%';
});

const maxWidth = computed(() => {
  if (props.width) {
    return `${props.width}px`;
  }
  if (calculatedWidth.value) {
    return `${calculatedWidth.value}px`;
  }
  return '100%';
});

const uploadMessageHeight = ref(0);
const uploadMessageWidth = ref(0);
function onImageContainerResize({ height, width }) {
  uploadMessageHeight.value = height;
  uploadMessageWidth.value = width;
}

function calculateDimensions() {
  if (!props.modelValue) {
    return;
  }

  if (props.width && props.height) {
    return;
  }

  isCalculatingDimensions.value = true;
  const imageEl = new Image();
  imageEl.src = props.modelValue;
  imageEl.onload = () => {
    const aspectRatio = imageEl.naturalWidth / imageEl.naturalHeight;

    if (props.width && !props.height) {
      calculatedHeight.value = Math.round(props.width / aspectRatio);
    } else if (!props.width && props.height) {
      calculatedWidth.value = Math.round(props.height * aspectRatio);
    }

    isCalculatingDimensions.value = false;
  };
}

onMounted(calculateDimensions);
watch(() => [props.modelValue, props.width, props.height], calculateDimensions);

async function pickFiles() {
  if (props.disable || props.readonly) {
    return;
  }
  imageInput.value?.pickFiles();
  await setTimeoutPromise(250);
  isHovering.value = false;
}

async function onImageUpdate(image: File | undefined) {
  if (image) {
    isHovering.value = false;
    isUploading.value = true;
    emit('upload');
    const response = await upload({ data: image });

    // Fetch the image so no secondary loading indicator is shown
    const imageEl = new Image();
    imageEl.onload = () => {
      calculatedWidth.value = imageEl.naturalWidth;
      calculatedHeight.value = imageEl.naturalHeight;
      isUploading.value = false;
      newImage.value = null;
      emit('uploaded');
      emit('update:model-value', response.secureUrl);
    };
    imageEl.src = response.secureUrl;
  }
}

const isHovering = ref(false);
const noHoverUntilMouseOut = ref(false);
const hoveringOverClear = ref(false);

async function onMouseEnter() {
  if (isUploading.value || noHoverUntilMouseOut.value) {
    noHoverUntilMouseOut.value = true;
    return;
  }
  isHovering.value = true;
}

function onMouseOver() {
  if (isUploading.value) {
    noHoverUntilMouseOut.value = true;
    isHovering.value = false;
    return;
  }
}

function onMouseLeave() {
  isHovering.value = false;
  noHoverUntilMouseOut.value = false;
}

async function onClearClick() {
  if (props.revert) {
    emit('revert');
  } else {
    emit('clear');
  }

  if (isTouchOnly.value) {
    // This purely to prevent the button from continuing to be
    // highlighted after tapping it on mobile
    hideClear.value = true;
    await setTimeoutPromise(100);
    hideClear.value = false;
    isHovering.value = false;
  }
}

/**
 * Drag-n-Drop
 */

const isDragging = ref(false);
const dragDepth = ref(0);
const invalidUpload = ref(false);
const multiUpload = ref(false);

function onDragEnter() {
  isDragging.value = true;
  dragDepth.value += 1;
}

function onDragOver(event) {
  validateDraggableItems(event);
}

function onDragLeave() {
  dragDepth.value = Math.max(0, dragDepth.value - 1);
  if (dragDepth.value === 0) {
    isDragging.value = false;
    invalidUpload.value = false;
    multiUpload.value = false;
  }
}

function onDrop(event: DragEvent) {
  dragDepth.value = 0;
  isDragging.value = false;

  const isValid = validateDraggableItems(event);
  if (!isValid) {
    invalidUpload.value = false;
    multiUpload.value = false;
    return;
  }

  const [file] = Array.from(event.dataTransfer.files || []);
  onImageUpdate(file);
}

function validateDraggableItems(event: DragEvent) {
  invalidUpload.value = false;
  multiUpload.value = false;

  const files = getDraggedItems(event);
  if (files.length > 1) {
    multiUpload.value = true;
    return false;
  }

  const images = files.filter(isImageFile);
  if (images.length === 0) {
    invalidUpload.value = true;
    return false;
  }

  return true;
}

function getDraggedItems(event) {
  return Array.from(event.dataTransfer?.items || []);
}

function isImageFile(file) {
  // MIME types like image/png, image/jpeg, image/webp, image/gif, image/svg+xml
  return typeof file.type === 'string' && file.type.startsWith('image/');
}
</script>

<style scoped lang="scss">
$breakpoint-xm-ish: 450px;

.bg-form-field {
  background-color: rgba(0, 0, 0, 0.05);
}

.picker {
  border: 3px dashed $primary;
  margin-right: 10px;
}

.border-white {
  border-color: $grey-4;
}

.border-black {
  border-color: black;
}

.action-buttons {
  margin: map-get($space-sm, 'y') 0 0;

  @media (min-width: $breakpoint-xm-ish) {
    margin: 0;
  }
}

.picker {
  padding: 5px 10px;

  &.hover {
    background-color: white !important;
  }

  @media (min-width: $breakpoint-xm-ish) {
    padding: 0 5px;
  }
}

.image-container {
  height: 100%;
  width: 100%;
}

.round .image-container {
  overflow: hidden;
  border-radius: 50%;
}

.button-layout.side-by-side .edit-button,
.button-layout.side-by-side .image-container {
  width: calc(100% - 50px);
}

.image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.edit-button {
  opacity: 0;
  transition: opacity 0.2s ease-out;

  &:hover {
    background-color: $primary !important;

    &:before {
      content: ' ';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: rgba(255, 255, 255, 0.9);
    }
  }
}

.picker.hover .edit-button {
  opacity: 1;
}

:deep(.q-btn__content) {
  max-height: 100%;
}
</style>
